// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-5 §2.5 / D-POT-7 — the OwlRx Plan of Treatment worklist's pure logic: the date filter and sort
// menu as server parameters, the stage and signature-badge dictionaries, the row adapter and the
// physician prefill read. No React, no styling. Filtering, sorting and paging run on the server
// (GET carerx/pot/worklist) — nothing here filters rows.
import type { StatusTone } from '../../api/status'
import { formatUsDate } from '../../api/format'
import type { PotDashboardRow, PotSignatureBadge } from '../pot/potApi'
import type { PotQueueStage, PotWorklistRow, PotWorklistSort, PotWorklistView } from '../../api/carerxPot'
import type { NurseRow } from '../../api/client'
import type { SearchableOption } from '../../components/common/SearchableSelect'
import { potRequest } from './carerxPotRequest'

export type PotDateFilter = 'all' | 'today' | '7d' | '30d'
export type PotSortBy = 'newest' | 'oldest' | 'patient' | 'longest'
/** The row carries created_at; a backend that also sends the e-sign / void stamps is shown as such. */
export type PotRow = PotDashboardRow & { updated_at?: string | null; voided_at?: string | null; voided_by?: string | null; signed_at?: string | null; sent_at?: string | null }

export const DATE_OPTIONS: { value: PotDateFilter; label: string }[] = [
  { value: 'all', label: 'All time' }, { value: 'today', label: 'Today' }, { value: '7d', label: 'Last 7 days' }, { value: '30d', label: 'Last 30 days' },
]
export const SORT_OPTIONS: { value: PotSortBy; label: string }[] = [
  { value: 'newest', label: 'Newest first' }, { value: 'oldest', label: 'Oldest first' }, { value: 'patient', label: 'Patient A-Z' }, { value: 'longest', label: 'Longest visit' },
]

export const lastTouched = (r: PotRow): string => r.updated_at || r.completed_at || r.created_at

/** The epoch floor a date filter admits (0 = everything). "Today" is local midnight. */
export function sinceOf(date: PotDateFilter, now = Date.now()): number {
  if (date === 'today') { const d = new Date(now); d.setHours(0, 0, 0, 0); return d.getTime() }
  if (date === '7d') return now - 7 * 864e5
  if (date === '30d') return now - 30 * 864e5
  return 0
}

export const minutesOf = (s: number | null | undefined): number | null => (s == null ? null : Math.round(s / 60))

// ── instrument chip (D-POT-5 P1: rows carry schema_code so mixed data is visible during the comparison) ──
export const CLASSIC_SCHEMA_CODE = 'pot_classic_v1'
export const isClassicRow = (r: Pick<PotRow, 'schema_code'>): boolean => r.schema_code === CLASSIC_SCHEMA_CODE

// ── signature badge (asp-pot spec §3.1) on the kit's status tones ──
export const SIG_BADGE: Record<PotSignatureBadge, { label: string; tone: StatusTone }> = {
  not_sent: { label: 'Not sent', tone: 'grey' },
  awaiting_physician: { label: 'Awaiting physician', tone: 'amber' },
  signed: { label: 'Signed', tone: 'green' },
  declined: { label: 'Declined', tone: 'red' },
  voided: { label: 'Voided', tone: 'red' },
}
/** The line under the badge: when it was sent / signed / voided (and by whom). */
export function sigDateLine(r: PotRow): string | null {
  if (r.signature === 'awaiting_physician') return `Sent ${formatUsDate(r.sent_at ?? r.completed_at)}`
  if (r.signature === 'signed') return formatUsDate(r.signed_at ?? r.completed_at)
  if (r.signature === 'voided') return `${formatUsDate(r.voided_at ?? r.completed_at ?? r.created_at)}${r.voided_by ? ` by ${r.voided_by}` : ''}`
  return null
}

// ── Reassign… / Share… nurse picker (both dashboards) ──
/** The nurses a POT can be reassigned or shared to. Both endpoints take an active LOGIN (accounts.id); a roster
 *  nurse with no login (an agency nurse not invited yet) is listed disabled with the reason — offering it
 *  turned every pick into a 422 nurse_not_found_or_inactive (D-POT-7 lab, 4 of CDI's 5 roster nurses). */
export function assignableNurseOptions(roster: readonly NurseRow[], currentNurseId: string | null | undefined): SearchableOption[] {
  return roster
    .filter(n => n.active && !!n.nurse_id)
    .map(n => {
      const loginOk = n.login?.status === 'active'
      const current = n.nurse_id === currentNurseId
      return { value: n.nurse_id, label: n.display_name || n.login?.name || n.login?.email || '(no name)',
        hint: current ? 'current nurse' : loginOk ? undefined : 'no login — cannot be assigned', disabled: current || !loginOk }
    })
    .sort((a, b) => Number(a.disabled) - Number(b.disabled) || a.label.localeCompare(b.label))
}

// ── routes ──
export const potFormPath = (visitId: string, view = false): string => `/carerx/pot/form/${visitId}${view ? '?view=1' : ''}`

// ── Send for Signature prefill: the physician the nurse typed in §14 (physName / physEmail) ──
export const isValidEmail = (s: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim())
export function physicianFromSections(sectionsJson: string | null | undefined): { name: string; email: string } {
  try {
    const s = JSON.parse(sectionsJson ?? '{}') as Record<string, Record<string, unknown> | undefined>
    return { name: String(s['14']?.physName ?? ''), email: String(s['14']?.physEmail ?? '') }
  } catch { return { name: '', email: '' } /* malformed draft JSON — the user types the physician in by hand */ }
}
/** Reads the row's own form (its instrument, so a classic-authored row prefills too). */
export async function readPhysician(visitId: string, schemaCode: string | null | undefined): Promise<{ name: string; email: string }> {
  const qs = schemaCode ? `?schema=${encodeURIComponent(schemaCode)}` : ''
  const r = await potRequest<{ form: { sections: string | null } | null }>('GET', `/carerx/visits/${visitId}/pot-form${qs}`)
  return physicianFromSections(r.form?.sections)
}

export const todayIso = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// ── D-POT-7 worklist: queue stage chip, the date filter as a server `since`, the sort mapping ──
export const QUEUE_STAGE: Record<PotQueueStage, { label: string; tone: StatusTone }> = {
  ready: { label: 'Ready to start', tone: 'green' },
  unclaimed: { label: 'Unclaimed', tone: 'amber' },
  assigned: { label: 'Assigned', tone: 'blue' },
  claimed: { label: 'Claimed', tone: 'blue' },
}
/** The date filter as the ISO instant the server compares against (undefined = all time). */
export const sinceIso = (date: PotDateFilter, now = Date.now()): string | undefined => {
  const t = sinceOf(date, now)
  return t ? new Date(t).toISOString() : undefined
}
export type PotWorklistSortChoice = PotSortBy | 'soonest'
/** The sort menu: the queue adds "Visit date (soonest)" and defaults to it. */
export const worklistSortOptions = (view: PotWorklistView): { value: PotWorklistSortChoice; label: string }[] =>
  view === 'queue' ? [{ value: 'soonest', label: 'Visit date (soonest)' }, ...SORT_OPTIONS.filter(o => o.value !== 'longest')] : SORT_OPTIONS
export const defaultSort = (view: PotWorklistView): PotWorklistSortChoice => (view === 'queue' ? 'soonest' : 'newest')
export function serverSort(choice: PotWorklistSortChoice): { sort: PotWorklistSort; dir: 'asc' | 'desc' } {
  switch (choice) {
    case 'soonest': return { sort: 'visit_date', dir: 'asc' }
    case 'oldest': return { sort: 'activity', dir: 'asc' }
    case 'patient': return { sort: 'patient', dir: 'asc' }
    case 'longest': return { sort: 'worked', dir: 'desc' }
    default: return { sort: 'activity', dir: 'desc' }
  }
}
export const isWorklistView = (v: unknown): v is PotWorklistView => v === 'queue' || v === 'in_progress' || v === 'completed' || v === 'voided'
/** A worklist visit row in the dashboard-row shape the In progress / Completed / Voided tables and dialogs take. */
export const asPotRow = (r: PotWorklistRow): PotRow => ({
  ...r, visit_id: r.visit_id ?? '', patient_name: r.patient_name ?? '', patient_initials: r.patient_initials ?? '',
  dob: null, status: r.status ?? 'scheduled', created_at: r.created_at ?? '',
})
